if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();
        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.dataset.sectionId}`);
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.dataset.sectionId}`);
        this.hiddenControlField.disabled = false;
        this.emailInput = this.querySelector(`#Recipient-email-${this.dataset.sectionId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.dataset.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.dataset.sectionId}`);
        if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();

        this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
        this.defaultErrorHeader = this.errorMessage?.innerText;
        this.currentProductVariantId = this.dataset.productVariantId;
        this.addEventListener('change', this.onChange.bind(this));
        this.onChange();
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.resetRecipientForm();
          }
        });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId === this.dataset.sectionId) {
            this.currentProductVariantId = event.data.variant.id.toString();
          }
        });

        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.displayErrorMessage(event.message, event.errors);
          }
        });

        this.messageCounter = this.querySelector(`#Recipient-message-counter-${this.dataset.sectionId}`);
        this.messageInput.addEventListener('input', this.updateCharCount.bind(this));
        this.updateCharCount();

        this.nameInput.addEventListener('blur', this.validateNameField.bind(this));
        this.emailInput.addEventListener('blur', this.validateEmailField.bind(this));
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }
      }

      onChange() {
        this.enableInputFields();
        this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormExpanded;
      }

      validateForm() {
        this.clearErrorMessage();
        let isValid = true;
        const errors = {};

        if (!this.nameInput.value.trim()) {
          errors.name = ['Name is required'];
          isValid = false;
        }

        if (!this.emailInput.value.trim()) {
          errors.email = ['Email is required'];
          isValid = false;
        } else if (!this.isValidEmail(this.emailInput.value.trim())) {
          errors.email = ['Please enter a valid email address'];
          isValid = false;
        }

        if (!isValid) {
          this.displayErrorMessage('Please fix the following errors:', errors);
        }

        return isValid;
      }

      isValidEmail(email) {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailPattern.test(email);
      }

      validateNameField() {
        const nameError = this.querySelector(`#RecipientForm-name-error-${this.dataset.sectionId}`);
        const nameErrorText = nameError.querySelector('.error-message');

        if (!this.nameInput.value.trim()) {
          nameError.classList.remove('hidden');
          nameErrorText.innerText = 'Name is required.';
          this.nameInput.setAttribute('aria-invalid', true);
          this.nameInput.setAttribute('aria-describedby', `RecipientForm-name-error-${this.dataset.sectionId}`);
        } else {
          nameError.classList.add('hidden');
          nameErrorText.innerText = '';
          this.nameInput.setAttribute('aria-invalid', false);
          this.nameInput.removeAttribute('aria-describedby');
        }
      }

      validateEmailField() {
        const emailError = this.querySelector(`#RecipientForm-email-error-${this.dataset.sectionId}`);
        const emailErrorText = emailError.querySelector('.error-message');

        if (!this.emailInput.value.trim()) {
          emailError.classList.remove('hidden');
          emailErrorText.innerText = 'Email is required.';
          this.emailInput.setAttribute('aria-invalid', true);
          this.emailInput.setAttribute('aria-describedby', `RecipientForm-email-error-${this.dataset.sectionId}`);
        } else if (!this.isValidEmail(this.emailInput.value.trim())) {
          emailError.classList.remove('hidden');
          emailErrorText.innerText = 'Please enter a valid email address.';
          this.emailInput.setAttribute('aria-invalid', true);
          this.emailInput.setAttribute('aria-describedby', `RecipientForm-email-error-${this.dataset.sectionId}`);
        } else {
          emailError.classList.add('hidden');
          emailErrorText.innerText = '';
          this.emailInput.setAttribute('aria-invalid', false);
          this.emailInput.removeAttribute('aria-describedby');
        }
      }

      inputFields() {
        return [this.emailInput, this.nameInput, this.messageInput];
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty];
      }

      clearInputFields() {
        this.inputFields().forEach((field) => (field.value = ''));
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = false));
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = true));
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();
        this.errorMessageWrapper.hidden = false;
        if (typeof body === 'object') {
          this.errorMessage.innerText = this.defaultErrorHeader;
          return Object.entries(body).forEach(([key, value]) => {
            const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
            const fieldSelector = `#Recipient-${key}-${this.dataset.sectionId}`;
            const message = `${value.join(', ')}`;
            const errorMessageElement = this.querySelector(`#${errorMessageId}`);
            const errorTextElement = errorMessageElement?.querySelector('.error-message');
            if (!errorTextElement) return;

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message));
            }

            errorTextElement.innerText = `${message}.`;
            errorMessageElement.classList.remove('hidden');

            const inputElement = this[`${key}Input`];
            if (!inputElement) return;

            inputElement.setAttribute('aria-invalid', true);
            inputElement.setAttribute('aria-describedby', errorMessageId);
          });
        }

        this.errorMessage.innerText = body;
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.innerText = message;
        li.appendChild(a);
        li.className = 'error-message';
        return li;
      }

      clearErrorMessage() {
        this.errorMessageWrapper.hidden = true;

        if (this.errorMessageList) this.errorMessageList.innerHTML = '';

        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) textField.innerText = '';
        });

        [this.emailInput, this.messageInput, this.nameInput].forEach((inputElement) => {
          inputElement.setAttribute('aria-invalid', false);
          inputElement.removeAttribute('aria-describedby');
        });
      }

      resetRecipientForm() {
        this.clearInputFields();
        this.clearErrorMessage();
      }

      updateCharCount() {
        const currentLength = this.messageInput.value.length;
        const maxLength = this.messageInput.getAttribute('maxlength') || 200;
        if (this.messageCounter) {
          this.messageCounter.textContent = `${currentLength}/${maxLength}`;
        }
      }
    }
  );
}
